import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { InformationIcon, InsuranceIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { getSuggestedInsurances } from '../../../api/insurances/get-suggested-insurances'
import { InsuranceTypeEnum } from '../../../lib/types/enums/insurance/insurance-type.enum'
import {
  EnrollmentPropositionInput,
  InsuranceEnrollmentPropositionInput
} from '../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { InsuranceLight } from '../../../lib/types/models/insurance/insurance'
import { EnrollmentPropositionInsuranceSelection } from './enrollment-form/enrollment-proposition-insurance-selection'

type Props = {
  cseCode?: string | null
  disabled?: boolean
  disableTypes?: InsuranceTypeEnum[]
  residenceCountry?: string
  destinationCountry?: number
  defaultValues?: InsuranceEnrollmentPropositionInput[]
  proposition?: EnrollmentPropositionInput
  isOpen: boolean
  onClose: () => void
  onSubmit: (enrollment: InsuranceLight[], additionalEmails?: string[]) => Promise<void>
}

const EnrollmentPropositionInsuranceModal: React.FC<Props> = ({
  cseCode,
  disabled,
  disableTypes,
  defaultValues,
  destinationCountry,
  proposition,
  residenceCountry,
  isOpen,
  onSubmit,
  onClose
}) => {
  const canLoadSuggestedInsurances = !!destinationCountry && !!residenceCountry

  const { data: insurances, isFetching } = useQuery({
    queryKey: ['suggested-insurances', destinationCountry, residenceCountry, cseCode],
    queryFn: () =>
      getSuggestedInsurances({
        cseCode,
        destinationCountryId: destinationCountry!,
        residenceCountry: residenceCountry!
      }),
    enabled: isOpen && canLoadSuggestedInsurances
  })

  if (canLoadSuggestedInsurances && insurances && !insurances.length) {
    return (
      <Modal
        isOpen={isOpen}
        title="Attention, avec cette adresse postale, LEC ne peut proposer aucune assurance aux parents."
        icon={InformationIcon}
        size="small"
        variant="confirmation"
        hasBorderBottom
        onCancel={onClose}
        actionButtonsOptions={{
          cancelText: 'Annuler',
          confirmText: 'Valider quand même',
          onConfirm: () => onSubmit([])
        }}
      />
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      title={'Assurances en option'}
      icon={InsuranceIcon}
      size="small"
      hideActions
      onCancel={onClose}
    >
      {canLoadSuggestedInsurances ? (
        <EnrollmentPropositionInsuranceSelection
          className={MODAL_BODY_CLASSNAME}
          cseCode={cseCode}
          disabled={disabled}
          disableTypes={disableTypes}
          defaultValues={defaultValues}
          isFetching={isFetching}
          insurances={insurances ?? []}
          proposition={proposition}
          onInsuranceChange={async (insurances, additionalEmails) => {
            await onSubmit(insurances, additionalEmails)
          }}
          onCancel={onClose}
        />
      ) : (
        <Typography variant="label-medium" color={tailwindColors.grey.lighter} className="p-4">
          Les options d'assurance ne peuvent pas être chargées car le pays de destination de votre
          voyage et/ou votre pays de résidence sont manquants. Veuillez vérifier vos informations
          et réessayer.
        </Typography>
      )}
    </Modal>
  )
}

export default EnrollmentPropositionInsuranceModal
